<template>
  <div class="admin-dashboard">
    <v-container class="py-6">
      <!-- Header -->
      <v-row class="mb-6">
        <v-col cols="12">
          <h1 class="text-h3 text-gradient mb-2">
            {{ $t('admin.dashboard') }}
          </h1>
          <p class="text-h6 text-medium-emphasis">
            {{ $t('admin.welcomeMessage') }}
          </p>
        </v-col>
      </v-row>

      <!-- Statistics Overview -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="primary" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-account-multiple" size="56" class="mb-3" />
              <div class="text-h3 font-weight-bold mb-2">{{ stats.totalUsers }}</div>
              <div class="text-body-1">{{ $t('admin.totalUsers') }}</div>
              <v-chip size="small" color="success" class="mt-2">
                <v-icon start size="small">mdi-arrow-up</v-icon>
                +{{ stats.newUsersThisWeek }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="success" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-account-tie" size="56" class="mb-3" />
              <div class="text-h3 font-weight-bold mb-2">{{ stats.totalInstructors }}</div>
              <div class="text-body-1">{{ $t('admin.instructors') }}</div>
              <v-chip size="small" color="info" class="mt-2">
                {{ stats.activeInstructors }} {{ $t('admin.active') }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="warning" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-test-tube" size="56" class="mb-3" />
              <div class="text-h3 font-weight-bold mb-2">{{ stats.totalTests }}</div>
              <div class="text-body-1">{{ $t('admin.testsCompleted') }}</div>
              <v-chip size="small" color="success" class="mt-2">
                {{ stats.testsToday }} {{ $t('admin.today') }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" color="info" variant="tonal" elevation="4">
            <v-card-text class="text-center pa-6">
              <v-icon icon="mdi-message" size="56" class="mb-3" />
              <div class="text-h3 font-weight-bold mb-2">{{ stats.totalMessages }}</div>
              <div class="text-body-1">{{ $t('admin.messages') }}</div>
              <v-chip size="small" color="warning" class="mt-2">
                {{ stats.unreadMessages }} {{ $t('admin.unread') }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Real-time Online Chart -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card class="online-realtime-card" elevation="4">
            <v-card-title class="d-flex align-center flex-wrap ga-2">
              <v-icon icon="mdi-pulse" color="success" class="me-2" />
              {{ $t('admin.onlineRealtime') || 'Қазір онлайн' }}
              <v-chip color="success" size="small" variant="flat" class="ms-2">
                <span class="online-pulse"></span>
                {{ onlineLive.count }}
              </v-chip>
              <v-chip
                v-if="onlineLive.inTestCount"
                color="warning"
                size="small"
                variant="tonal"
                prepend-icon="mdi-pencil"
              >
                {{ $t('admin.inTest') || 'Тест ішінде' }}: {{ onlineLive.inTestCount }}
              </v-chip>
              <v-spacer />
              <span class="text-caption text-medium-emphasis">
                {{ $t('admin.updatedAt') || 'Жаңартылды' }}: {{ onlineLive.lastUpdate }}
              </span>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="d-flex align-stretch ga-4 flex-wrap">
                <!-- Sparkline -->
                <div class="online-sparkline-wrap flex-grow-1">
                  <svg :viewBox="`0 0 ${sparklineWidth} 80`" preserveAspectRatio="none" class="online-sparkline">
                    <polyline
                      :points="sparklinePoints"
                      fill="none"
                      stroke="rgb(76, 175, 80)"
                      stroke-width="2"
                    />
                    <polygon :points="sparklineArea" fill="rgba(76, 175, 80, 0.12)" />
                  </svg>
                  <div class="sparkline-axis text-caption text-medium-emphasis d-flex justify-space-between">
                    <span>{{ Math.floor(onlineLive.history.length * 5 / 60) }} мин артта</span>
                    <span>{{ $t('admin.now') || 'Қазір' }}</span>
                  </div>
                </div>

                <!-- By role -->
                <div class="online-byrole">
                  <div
                    v-for="(count, role) in onlineLive.byRole"
                    :key="role"
                    class="role-row"
                  >
                    <v-icon size="small" :color="roleColor(role)" class="me-2">{{ roleIcon(role) }}</v-icon>
                    <span class="role-name">{{ $t('roles.' + role) || role }}</span>
                    <span class="role-count">{{ count }}</span>
                  </div>
                  <div v-if="!Object.keys(onlineLive.byRole).length" class="text-caption text-medium-emphasis">
                    {{ $t('admin.noOneOnline') || 'Ешкім онлайн емес' }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Row -->
      <v-row class="mb-6">
        <!-- User Activity Chart -->
        <v-col cols="12" lg="8">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center cursor-pointer" @click="toggleDashboardSection('notifications')">
              <v-icon icon="mdi-chart-line" class="me-2" />
              {{ $t('admin.userActivity') }}
              <v-spacer />
              <v-btn-toggle v-if="sectionsOpen.notifications" v-model="sectionSort.notifications" density="compact" mandatory class="me-2" @click.stop>
                <v-btn value="desc" size="x-small">Жаңа</v-btn>
                <v-btn value="asc" size="x-small">Ескі</v-btn>
              </v-btn-toggle>
              <v-btn-toggle v-model="activityPeriod" density="compact" mandatory>
                <v-btn value="week" size="small">{{ $t('admin.week') }}</v-btn>
                <v-btn value="month" size="small">{{ $t('admin.month') }}</v-btn>
                <v-btn value="year" size="small">{{ $t('admin.year') }}</v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="chart-container pa-4">
                <!-- Simple bar chart visualization -->
                <div class="activity-chart">
                  <div
                    v-for="(day, index) in activityData"
                    :key="index"
                    class="chart-bar"
                  >
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <div class="bar-container" v-bind="props">
                          <div
                            class="bar"
                            :style="{ height: `${(day.value / maxActivity) * 100}%` }"
                          >
                          </div>
                        </div>
                      </template>
                      <div>{{ day.label }}</div>
                      <div>Қолданушылар: {{ day.users || 0 }}</div>
                      <div>Тесттер: {{ day.tests || 0 }}</div>
                      <div><strong>Жалпы: {{ day.value }}</strong></div>
                    </v-tooltip>
                    <div class="bar-label">{{ day.label }}</div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Test Results Distribution -->
        <v-col cols="12" lg="4">
          <v-card elevation="4">
            <v-card-title>
              <v-icon icon="mdi-chart-donut" class="me-2" />
              {{ $t('admin.testResults') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="donut-chart">
                <v-progress-circular
                  :model-value="testDistribution.passed"
                  :size="200"
                  :width="20"
                  color="success"
                  class="mb-4"
                >
                  <div class="text-center">
                    <div class="text-h4 font-weight-bold">{{ testDistribution.passed }}%</div>
                    <div class="text-body-2">{{ $t('admin.passed') }}</div>
                  </div>
                </v-progress-circular>
                
                <v-list density="compact" class="bg-transparent">
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-circle" color="success" size="small" />
                    </template>
                    <v-list-item-title>
                      {{ $t('admin.passed') }}: {{ testDistribution.passedCount }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item>
                    <template #prepend>
                      <v-icon icon="mdi-circle" color="error" size="small" />
                    </template>
                    <v-list-item-title>
                      {{ $t('admin.failed') }}: {{ testDistribution.failedCount }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Recent Activities & Notifications -->
      <v-row>
        <!-- Admin Notifications -->
        <v-col cols="12" lg="6">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center cursor-pointer" @click="toggleDashboardSection('activities')">
              <v-icon icon="mdi-bell" class="me-2" />
              {{ $t('admin.notifications') }}
              <v-spacer />
              <v-btn-toggle v-if="sectionsOpen.activities" v-model="sectionSort.activities" density="compact" mandatory class="me-2" @click.stop>
                <v-btn value="desc" size="x-small">Жаңа</v-btn>
                <v-btn value="asc" size="x-small">Ескі</v-btn>
              </v-btn-toggle>
              <v-chip 
                color="warning" 
                size="small" 
                v-if="unreadNotificationsCount > 0"
                :class="{ 'notification-badge': hasNewNotifications }"
              >
                {{ unreadNotificationsCount }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text v-if="sectionsOpen.notifications" class="pa-0">
              <v-progress-linear v-if="sectionLoading.notifications" indeterminate color="primary" />
              <v-list>
                <v-list-item
                  v-for="notification in sortedNotifications"
                  :key="notification.id"
                  class="notification-item"
                  :class="{ 'notification-unread': !notification.isRead }"
                  @click="markAsRead(notification.id)"
                >
                  <template #prepend>
                    <v-avatar :color="getNotificationColor(notification.type)">
                      <v-icon :icon="getNotificationIcon(notification.type)" />
                    </v-avatar>
                  </template>

                  <v-list-item-title>{{ notification.title }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ notification.message }}
                  </v-list-item-subtitle>

                  <template #append>
                    <div class="text-caption text-medium-emphasis">
                      {{ formatTime(notification.createdAt) }}
                    </div>
                  </template>
                </v-list-item>
              </v-list>

              <div v-if="notifications.length === 0 && !sectionLoading.notifications" class="text-center pa-8">
                <v-icon icon="mdi-bell-check" size="64" class="text-medium-emphasis mb-2" />
                <p class="text-medium-emphasis">{{ $t('admin.noNotifications') }}</p>
              </div>

              <div v-if="notifications.length > 0 && notificationsPagination.hasMore" class="text-center pa-3">
                <v-btn
                  variant="tonal"
                  size="small"
                  color="primary"
                  :loading="notificationsPagination.loading"
                  prepend-icon="mdi-chevron-down"
                  @click="loadMoreNotifications"
                >
                  {{ $t('common.loadMore') || 'Тағы жүктеу' }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Recent Activities -->
        <v-col cols="12" lg="6">
          <v-card elevation="4">
            <v-card-title class="d-flex align-center">
              <v-icon icon="mdi-history" class="me-2" />
              {{ $t('admin.recentActivities') }}
              <v-spacer />
              <v-btn
                color="primary"
                size="small"
                variant="outlined"
                @click="$router.push('/admin/actions')"
              >
                <v-icon start size="small">mdi-shield-crown</v-icon>
                Admin әрекеттері
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text v-if="sectionsOpen.activities" class="pa-0">
              <v-list>
                <v-list-item
                  v-for="activity in sortedRecentActivities"
                  :key="activity.id"
                  class="activity-item"
                >
                  <template #prepend>
                    <v-avatar :color="getActivityColor(activity.type)">
                      <v-icon :icon="getActivityIcon(activity.type)" />
                    </v-avatar>
                  </template>

                  <v-list-item-title>{{ activity.title }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ activity.description }}
                  </v-list-item-subtitle>

                  <template #append>
                    <span class="text-caption text-medium-emphasis">
                      {{ formatTime(activity.timestamp) }}
                    </span>
                  </template>
                </v-list-item>
              </v-list>

              <div v-if="recentActivities.length === 0" class="text-center pa-8">
                <v-icon icon="mdi-history" size="64" class="text-medium-emphasis mb-2" />
                <p class="text-medium-emphasis">{{ $t('admin.noActivities') }}</p>
              </div>

              <div v-if="recentActivities.length > 0 && activitiesPagination.hasMore" class="text-center pa-3">
                <v-btn
                  variant="tonal"
                  size="small"
                  color="primary"
                  :loading="activitiesPagination.loading"
                  prepend-icon="mdi-chevron-down"
                  @click="loadMoreActivities"
                >
                  {{ $t('common.loadMore') || 'Тағы жүктеу' }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Pending Users -->
        <v-col cols="12" lg="6">
          <v-card 
            elevation="4" 
            :class="{ 'pending-users-card': pendingUsers.length > 0 }"
            class="position-relative overflow-hidden"
          >
            <!-- Animated gradient overlay -->
            <div 
              v-if="pendingUsers.length > 0" 
              class="gradient-animation"
            ></div>
            
            <v-card-title class="d-flex align-center position-relative cursor-pointer" @click="toggleDashboardSection('pending')">
              <v-icon icon="mdi-account-clock" class="me-2" />
              {{ $t('admin.pendingUsers') }}
              <v-spacer />
              <v-btn-toggle v-if="sectionsOpen.pending" v-model="sectionSort.pending" density="compact" mandatory class="me-2" @click.stop>
                <v-btn value="desc" size="x-small">Жаңа</v-btn>
                <v-btn value="asc" size="x-small">Ескі</v-btn>
              </v-btn-toggle>
              <v-chip 
                color="warning" 
                size="small" 
                v-if="pendingUsers.length > 0"
                :class="{ 'notification-badge': hasNewPendingUsers }"
              >
                {{ pendingUsers.length }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text v-if="sectionsOpen.pending" class="pa-0">
              <v-progress-linear v-if="sectionLoading.pending" indeterminate color="primary" />
              <v-list>
                <v-list-item
                  v-for="user in sortedPendingUsers"
                  :key="user.id"
                  class="user-item"
                >
                  <template #prepend>
                    <v-avatar :color="user.status === 'PASSWORD_RESET' ? 'orange' : 'warning'">
                      <v-icon>{{ user.status === 'PASSWORD_RESET' ? 'mdi-lock-reset' : 'mdi-account' }}</v-icon>
                    </v-avatar>
                  </template>

                  <v-list-item-title>{{ user.fio }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ user.phone }} • {{ formatDate(user.createdAt) }}
                    <v-chip 
                      v-if="user.status === 'PASSWORD_RESET'" 
                      size="x-small" 
                      color="orange"
                      class="ml-2"
                    >
                      Құпия сөз
                    </v-chip>
                  </v-list-item-subtitle>

                  <template #append>
                    <v-btn
                      v-if="user.status === 'PASSWORD_RESET'"
                      icon
                      size="small"
                      color="primary"
                      variant="text"
                      @click="approvePasswordReset(user)"
                    >
                      <v-icon>mdi-send</v-icon>
                    </v-btn>
                    <template v-else>
                      <v-btn
                        icon
                        size="small"
                        color="success"
                        variant="text"
                        @click="approveUser(user.id)"
                      >
                        <v-icon>mdi-check</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        color="error"
                        variant="text"
                        @click="rejectUser(user.id)"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </template>
                  </template>
                </v-list-item>
              </v-list>

              <div v-if="pendingUsers.length === 0 && !sectionLoading.pending" class="text-center pa-8">
                <v-icon icon="mdi-check-circle" size="64" color="success" class="mb-2" />
                <p class="text-medium-emphasis">{{ $t('admin.noPendingUsers') }}</p>
              </div>

              <div v-if="pendingUsers.length > 0 && pendingPagination.hasMore" class="text-center pa-3">
                <v-btn
                  variant="tonal"
                  size="small"
                  color="primary"
                  :loading="pendingPagination.loading"
                  prepend-icon="mdi-chevron-down"
                  @click="loadMorePending"
                >
                  {{ $t('common.loadMore') || 'Тағы жүктеу' }}
                </v-btn>
              </div>
            </v-card-text>
            <v-card-actions v-if="pendingUsers.length > 0">
              <v-btn
                block
                color="primary"
                @click="$router.push('/admin/users')"
              >
                {{ $t('admin.viewAllUsers') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSnackbarStore } from '@/stores/snackbar'
import { useSocketStore } from '@/stores/socket'
import api from '@/utils/api'

const { t } = useI18n()
const router = useRouter()
const snackbarStore = useSnackbarStore()
const socketStore = useSocketStore()

// Refs
const loading = ref(false)
const activityPeriod = ref('week')
const notifications = ref([])
const notificationsPagination = ref({ page: 1, hasMore: true, loading: false })
const pendingPagination = ref({ page: 1, hasMore: true, loading: false })
const activitiesPagination = ref({ page: 1, hasMore: true, loading: false })
const hasNewNotifications = ref(false)
const hasNewPendingUsers = ref(false)
const sectionsOpen = ref({
  notifications: false,
  activities: false,
  pending: false
})
const sectionLoading = ref({
  notifications: false,
  pending: false
})
const sectionSort = ref({
  notifications: 'desc',
  activities: 'desc',
  pending: 'desc'
})
const stats = ref({
  totalUsers: 0,
  newUsersThisWeek: 0,
  totalInstructors: 0,
  activeInstructors: 0,
  totalTests: 0,
  testsToday: 0,
  totalMessages: 0,
  unreadMessages: 0
})

const activityData = ref([])

// ========= Online realtime =========
const MAX_HISTORY_POINTS = 60 // ~5 min at 5s interval
const POLL_INTERVAL_MS = 5000

const onlineLive = ref({
  count: 0,
  inTestCount: 0,
  byRole: {},
  history: [],
  lastUpdate: '—'
})

let onlineTimer = null

const fetchOnlineSnapshot = async () => {
  try {
    const res = await api.get('/admin/users/online')
    const data = res.data || {}
    const history = onlineLive.value.history.slice()
    history.push(Number(data.count) || 0)
    if (history.length > MAX_HISTORY_POINTS) history.shift()
    onlineLive.value = {
      count: Number(data.count) || 0,
      inTestCount: Number(data.inTestCount) || 0,
      byRole: data.byRole || {},
      history,
      lastUpdate: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  } catch (_) { /* ignore */ }
}

const sparklineWidth = 600
const sparklineHeight = 80

const sparklinePoints = computed(() => {
  const data = onlineLive.value.history
  if (!data.length) return ''
  const max = Math.max(1, ...data)
  const step = sparklineWidth / Math.max(1, MAX_HISTORY_POINTS - 1)
  return data.map((v, i) => {
    const x = i * step
    const y = sparklineHeight - (v / max) * (sparklineHeight - 6) - 3
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
})

const sparklineArea = computed(() => {
  const pts = sparklinePoints.value
  if (!pts) return ''
  const data = onlineLive.value.history
  const step = sparklineWidth / Math.max(1, MAX_HISTORY_POINTS - 1)
  const lastX = (data.length - 1) * step
  return `0,${sparklineHeight} ${pts} ${lastX.toFixed(1)},${sparklineHeight}`
})

const roleColor = (role) => {
  switch (role) {
    case 'ADMIN':
    case 'ADMIN_LIMITED': return 'red'
    case 'INSTRUCTOR': return 'primary'
    case 'STUDENT': return 'success'
    case 'LIMITED_STUDENT': return 'warning'
    case 'THEORY_TEACHER': return 'info'
    default: return 'grey'
  }
}

const roleIcon = (role) => {
  switch (role) {
    case 'ADMIN':
    case 'ADMIN_LIMITED': return 'mdi-shield-crown'
    case 'INSTRUCTOR': return 'mdi-account-tie'
    case 'STUDENT':
    case 'LIMITED_STUDENT': return 'mdi-account-school'
    case 'THEORY_TEACHER': return 'mdi-school'
    default: return 'mdi-account'
  }
}
const testDistribution = ref({
  passed: 0,
  passedCount: 0,
  failedCount: 0
})
const recentActivities = ref([])
const pendingUsers = ref([])
const systemStatus = ref({
  storageUsed: 2.5,
  storageTotal: 10,
  lastBackup: '2 hours ago'
})

// Computed
const maxActivity = computed(() => {
  return Math.max(...activityData.value.map(d => d.value), 1)
})

const unreadNotificationsCount = computed(() => {
  return notifications.value.filter(n => !n.isRead).length
})

const sortByDate = (items, direction, field) => {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    const aTime = new Date(a[field] || 0).getTime()
    const bTime = new Date(b[field] || 0).getTime()
    return (aTime - bTime) * multiplier
  })
}

const sortedNotifications = computed(() => sortByDate(notifications.value, sectionSort.value.notifications, 'createdAt'))
const sortedRecentActivities = computed(() => sortByDate(recentActivities.value, sectionSort.value.activities, 'timestamp'))
const sortedPendingUsers = computed(() => sortByDate(pendingUsers.value, sectionSort.value.pending, 'createdAt'))

// Methods
const loadDashboardData = async () => {
  loading.value = true
  try {
    // Load all dashboard data from single endpoint
    const response = await api.get('/admin/dashboard')
    const data = response.data

    // Set statistics - all real data from backend
    stats.value = {
      totalUsers: data.overview.totalUsers || 0,
      newUsersThisWeek: data.overview.newUsersThisWeek || 0,
      totalInstructors: data.overview.totalInstructors || 0,
      activeInstructors: data.overview.activeUsers || 0,
      totalTests: data.overview.totalTestResults || 0,
      testsToday: data.overview.testsToday || 0,
      totalMessages: data.overview.totalMessages || 0,
      unreadMessages: data.overview.unreadMessages || 0
    }

    // Load test distribution from backend (score >= 70 is passed)
    if (data.testDistribution) {
      testDistribution.value = {
        passed: data.testDistribution.passed || 0,
        passedCount: data.testDistribution.passedCount || 0,
        failedCount: data.testDistribution.failedCount || 0
      }
    }

    // Recent activities are now loaded from the same API
    if (data.recentActivity) {
      recentActivities.value = data.recentActivity.map(activity => ({
        ...activity,
        timestamp: new Date(activity.timestamp)
      }))
    }

    // Update system status with real data
    if (data.systemStatus) {
      systemStatus.value = {
        storageUsed: data.systemStatus.storageUsed,
        storageTotal: data.systemStatus.storageTotal,
        lastBackup: data.systemStatus.lastBackup,
        storageDetails: data.systemStatus.storageDetails
      }
    }

    // Load activity data (chart data) - now gets real data from backend with current period
    await loadActivityData(activityPeriod.value)

    // Heavy lists are opened as dropdowns and loaded lazily.
    if (sectionsOpen.value.pending) {
      await loadPendingUsersPaginated(true)
    }
    if (sectionsOpen.value.notifications) {
      await loadNotifications(true)
    }
    if (sectionsOpen.value.activities) {
      await loadRecentActivities(true)
    }

  } catch (error) {
    // Error
    snackbarStore.error('Dashboard мәліметтерін жүктеуде қате')
  } finally {
    loading.value = false
  }
}

const toggleDashboardSection = async (section) => {
  sectionsOpen.value[section] = !sectionsOpen.value[section]

  if (section === 'notifications' && sectionsOpen.value.notifications && notifications.value.length === 0) {
    await loadNotifications(true)
  }

  if (section === 'pending' && sectionsOpen.value.pending && pendingUsers.value.length === 0) {
    await loadPendingUsersPaginated(true)
  }

  if (section === 'activities' && sectionsOpen.value.activities && recentActivities.value.length === 0) {
    await loadRecentActivities(true)
  }
}

// Reload data when sort changes
watch(() => sectionSort.value.notifications, () => {
  if (sectionsOpen.value.notifications) loadNotifications(true)
})
watch(() => sectionSort.value.pending, () => {
  if (sectionsOpen.value.pending) loadPendingUsersPaginated(true)
})
watch(() => sectionSort.value.activities, () => {
  if (sectionsOpen.value.activities) loadRecentActivities(true)
})

const loadPendingUsers = async () => {
  sectionLoading.value.pending = true
  try {
    const pendingRes = await api.get('/admin/pending')
    pendingUsers.value = pendingRes.data.users || []
  } catch (error) {
    pendingUsers.value = []
  } finally {
    sectionLoading.value.pending = false
  }
}

const loadActivityData = async (period = 'week') => {
  try {
    // Load real activity data from backend with period filter
    const response = await api.get('/admin/dashboard/activity', {
      params: { period }
    });
    
    if (response.data.activityData && response.data.activityData.length > 0) {
      activityData.value = response.data.activityData.map(day => ({
        label: day.label,
        value: day.value,
        users: day.users,
        tests: day.tests
      }));
    } else {
      // Fallback to empty data if no activity
      const defaultLabels = period === 'month' 
        ? ['1-апта', '2-апта', '3-апта', '4-апта']
        : period === 'year'
        ? ['Қаң', 'Ақп', 'Нау', 'Сәу', 'Мам', 'Мау', 'Шіл', 'Там', 'Қыр', 'Қаз', 'Қар', 'Жел']
        : ['Жк', 'Дс', 'Сс', 'Ср', 'Бс', 'Жм', 'Сн'];
        
      activityData.value = defaultLabels.map(label => ({
        label,
        value: 0,
        users: 0,
        tests: 0
      }));
    }
  } catch (error) {
    // Error - use empty data
    const defaultLabels = period === 'month' 
      ? ['1-апта', '2-апта', '3-апта', '4-апта']
      : period === 'year'
      ? ['Қаң', 'Ақп', 'Нау', 'Сәу', 'Мам', 'Мау', 'Шіл', 'Там', 'Қыр', 'Қаз', 'Қар', 'Жел']
      : ['Жк', 'Дс', 'Сс', 'Ср', 'Бс', 'Жм', 'Сн'];
      
    activityData.value = defaultLabels.map(label => ({
      label,
      value: 0,
      users: 0,
      tests: 0
    }));
  }
}

const getActivityColor = (type) => {
  const colors = {
    user_registered: 'primary',
    test_completed: 'success',
    instructor_added: 'info',
    message_sent: 'warning',
    admin_action: 'purple'
  }
  return colors[type] || 'default'
}

const getStorageColor = (ratio) => {
  if (ratio >= 0.9) return 'error'
  if (ratio >= 0.7) return 'warning'
  if (ratio >= 0.5) return 'info'
  return 'success'
}

const getActivityIcon = (type) => {
  const icons = {
    user_registered: 'mdi-account-plus',
    test_completed: 'mdi-test-tube',
    instructor_added: 'mdi-account-tie',
    message_sent: 'mdi-message',
    admin_action: 'mdi-shield-crown'
  }
  return icons[type] || 'mdi-information'
}

const formatTime = (timestamp) => {
  const now = new Date()
  const diff = now - new Date(timestamp)
  const minutes = Math.floor(diff / 60000)
  
  if (minutes < 1) return t('admin.justNow')
  if (minutes < 60) return `${minutes} ${t('admin.minutesAgo')}`
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ${t('admin.hoursAgo')}`
  
  const days = Math.floor(hours / 24)
  return `${days} ${t('admin.daysAgo')}`
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const approveUser = async (userId) => {
  try {
    // Generate temporary password
    const temporaryPassword = Math.random().toString(36).slice(-8);
    
    const response = await api.post(`/admin/users/${userId}/approve`, {
      temporaryPassword
    });
    
    snackbarStore.show(t('admin.approveSuccess'), 'success');
    
    // Get user info for WhatsApp
    const user = pendingUsers.value.find(u => u.id === userId);
    if (user) {
      // Open WhatsApp with message
      openWhatsApp(user.phone, temporaryPassword, user.fio);
    }
    
    pendingUsers.value = pendingUsers.value.filter(u => u.id !== userId);
  } catch (error) {
    // Error;
    snackbarStore.show(t('admin.approveError'), 'error');
  }
}

const rejectUser = async (userId) => {
  try {
    await api.delete(`/admin/users/${userId}`)
    snackbarStore.show(t('admin.rejectSuccess'), 'success')
    pendingUsers.value = pendingUsers.value.filter(u => u.id !== userId)
  } catch (error) {
    // Error
    snackbarStore.show(t('admin.rejectError'), 'error')
  }
}

const approvePasswordReset = async (request) => {
  try {
    const response = await api.post(`/admin/requests/${request.requestId}/approve-reset`);
    
    snackbarStore.success('Құпия сөз коды дайын!');
    
    // Open WhatsApp with reset code
    openWhatsAppForReset(request.phone, response.data.resetCode, request.fio);
    
    // Remove from pending list
    pendingUsers.value = pendingUsers.value.filter(u => u.id !== request.id);
  } catch (error) {
    // Error;
    snackbarStore.error('Қате орын алды');
  }
}

// Notifications functions
const NOTIFICATIONS_PAGE_SIZE = 20

const loadNotifications = async (reset = true) => {
  if (notificationsPagination.value.loading) return
  if (reset) {
    notifications.value = []
    notificationsPagination.value = { page: 1, hasMore: true, loading: true }
  } else {
    if (!notificationsPagination.value.hasMore) return
    notificationsPagination.value.loading = true
  }
  sectionLoading.value.notifications = reset
  try {
    const response = await api.get('/admin/notifications', {
      params: {
        page: notificationsPagination.value.page,
        limit: NOTIFICATIONS_PAGE_SIZE,
        sort: sectionSort.value.notifications
      }
    })
    const next = response.data.notifications || []
    notifications.value = reset ? next : [...notifications.value, ...next]
    notificationsPagination.value.hasMore = response.data.pagination?.hasMore ?? false
    notificationsPagination.value.page += 1
  } catch (error) {
    if (reset) notifications.value = []
  } finally {
    sectionLoading.value.notifications = false
    notificationsPagination.value.loading = false
  }
}

const loadMoreNotifications = () => loadNotifications(false)

const PENDING_PAGE_SIZE = 20

const loadPendingUsersPaginated = async (reset = true) => {
  if (pendingPagination.value.loading) return
  if (reset) {
    pendingUsers.value = []
    pendingPagination.value = { page: 1, hasMore: true, loading: true }
  } else {
    if (!pendingPagination.value.hasMore) return
    pendingPagination.value.loading = true
  }
  sectionLoading.value.pending = reset
  try {
    const res = await api.get('/admin/pending', {
      params: {
        page: pendingPagination.value.page,
        limit: PENDING_PAGE_SIZE,
        sort: sectionSort.value.pending
      }
    })
    const next = res.data.users || []
    pendingUsers.value = reset ? next : [...pendingUsers.value, ...next]
    pendingPagination.value.hasMore = res.data.pagination?.hasMore ?? false
    pendingPagination.value.page += 1
  } catch (e) {
    if (reset) pendingUsers.value = []
  } finally {
    sectionLoading.value.pending = false
    pendingPagination.value.loading = false
  }
}

const loadMorePending = () => loadPendingUsersPaginated(false)

const ACTIVITIES_PAGE_SIZE = 20

const loadRecentActivities = async (reset = true) => {
  if (activitiesPagination.value.loading) return
  if (reset) {
    recentActivities.value = []
    activitiesPagination.value = { page: 1, hasMore: true, loading: true }
  } else {
    if (!activitiesPagination.value.hasMore) return
    activitiesPagination.value.loading = true
  }
  try {
    const res = await api.get('/admin/admin-actions', {
      params: {
        page: activitiesPagination.value.page,
        limit: ACTIVITIES_PAGE_SIZE,
        sort: sectionSort.value.activities
      }
    })
    const items = (res.data.actions || []).map(a => ({
      id: `admin_${a.id}`,
      type: 'admin_action',
      title: 'Admin әрекеті',
      description: `${a.adminName}: ${a.description}`,
      timestamp: a.timestamp,
      adminAction: a.action
    }))
    recentActivities.value = reset ? items : [...recentActivities.value, ...items]
    activitiesPagination.value.hasMore = res.data.pagination?.hasMore ?? false
    activitiesPagination.value.page += 1
  } catch (e) {
    if (reset) recentActivities.value = []
  } finally {
    activitiesPagination.value.loading = false
  }
}

const loadMoreActivities = () => loadRecentActivities(false)

const markAsRead = async (notificationId) => {
  try {
    await api.patch(`/admin/notifications/${notificationId}/read`)
    const notification = notifications.value.find(n => n.id === notificationId)
    if (notification) {
      notification.isRead = true
    }
  } catch (error) {
    // Error
  }
}

const getNotificationColor = (type) => {
  switch (type) {
    case 'WARNING': return 'warning'
    case 'ERROR': return 'error'
    case 'SUCCESS': return 'success'
    default: return 'primary'
  }
}

const getNotificationIcon = (type) => {
  switch (type) {
    case 'WARNING': return 'mdi-alert'
    case 'ERROR': return 'mdi-alert-circle'
    case 'SUCCESS': return 'mdi-check-circle'
    default: return 'mdi-information'
  }
}

// WhatsApp integration
const openWhatsApp = (phoneNumber, temporaryPassword, userName) => {
  try {
    // Clean phone number (remove + and spaces)
    const cleanPhone = phoneNumber.replace(/[\+\s\-\(\)]/g, '');
    
    // Create minimal WhatsApp message (no icons)
    const message = `Сәлеметсіз бе, ${userName}!

Сіздің Көркем Авто мектебіне тіркелуіңіз расталды.
Тіркелу деректері:
- Телефон: ${phoneNumber}
- Уақытша пароль: ${temporaryPassword}
Кіру үшін сайтқа өтіп, телефон нөміріңіз бен уақытша пароліңізді енгізіңіз. Профильде жаңа пароль орнатыңыз.
Құрметпен, Көркем Авто`;

    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
  } catch (error) {
    // Error;
  }
}

const openWhatsAppForReset = (phoneNumber, resetCode, userName) => {
  try {
    // Clean phone number (remove + and spaces)
    const cleanPhone = phoneNumber.replace(/[\+\s\-\(\)]/g, '');
    
    // Create minimal WhatsApp message for password reset (no icons)
    const message = `Сәлеметсіз бе, ${userName}!

Құпия сөзді қалпына келтіру сұранысыңыз расталды.

Деректер:
- Телефон: ${phoneNumber}
- Қалпына келтіру коды: ${resetCode}

Сайттағы "Құпия сөзді ұмыттыңыз ба?" бөлімінде осы кодты енгізіп, жаңа құпия сөз орнатыңыз.

Құрметпен, Көркем Авто`;

    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
  } catch (error) {
    // Error;
  }
}

// Watch for activity period changes
watch(activityPeriod, async (newPeriod) => {
  await loadActivityData(newPeriod)
})

// Lifecycle
onMounted(() => {
  loadDashboardData()
  setupRealTimeUpdates()
  // Start polling online snapshot
  fetchOnlineSnapshot()
  onlineTimer = setInterval(fetchOnlineSnapshot, POLL_INTERVAL_MS)
})

onUnmounted(() => {
  cleanupRealTimeUpdates()
  if (onlineTimer) {
    clearInterval(onlineTimer)
    onlineTimer = null
  }
})

// Real-time updates
const setupRealTimeUpdates = () => {
  // Log removed
  
  // Listen for new notifications
  socketStore.onNewNotification((notification) => {
    // Log removed
    notifications.value.unshift(notification)
    
    // Visual feedback
    showNotificationAlert(notification)
    
    // Update counters
    loadDashboardData()
  })
  
  // Listen for user registration updates
  socketStore.onUserRegistered(() => {
    // Log removed
    loadDashboardData()
  })
  
  // Listen for pending users updates
  socketStore.onPendingUsersUpdate(() => {
    // Log removed
    
    // Set blinking animation
    hasNewPendingUsers.value = true
    setTimeout(() => {
      hasNewPendingUsers.value = false
    }, 5000)
    
    loadDashboardData()
  })
}

const cleanupRealTimeUpdates = () => {
  // Remove socket listeners when component unmounts
  socketStore.removeAllListeners()
}

const showNotificationAlert = async (notification) => {
  // Set blinking state
  hasNewNotifications.value = true
  setTimeout(() => {
    hasNewNotifications.value = false
  }, 5000)
  
  // Add pulsing animation to notification card
  await nextTick()
  const notificationCard = document.querySelector('.notification-item')
  if (notificationCard) {
    notificationCard.classList.add('notification-new')
    setTimeout(() => {
      notificationCard.classList.remove('notification-new')
    }, 3000)
  }
  
  // Show snackbar
  snackbarStore.show({
    text: `${notification.title}: ${notification.message}`,
    color: 'info',
    timeout: 5000
  })
}
</script>

<style scoped>
.admin-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
}

.online-realtime-card {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(33, 33, 33, 0.4) 100%) !important;
}

.online-pulse {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  background: #fff;
  animation: online-pulse-anim 1.5s ease-in-out infinite;
}

@keyframes online-pulse-anim {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}

.online-sparkline-wrap {
  min-width: 260px;
}

.online-sparkline {
  width: 100%;
  height: 80px;
  display: block;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.12);
}

.sparkline-axis {
  margin-top: 4px;
}

.online-byrole {
  min-width: 220px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.online-byrole .role-row {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.online-byrole .role-name {
  flex: 1;
  color: rgba(255, 255, 255, 0.85);
}

.online-byrole .role-count {
  font-weight: 700;
  color: #fff;
  background: rgba(76, 175, 80, 0.2);
  padding: 2px 10px;
  border-radius: 999px;
  min-width: 30px;
  text-align: center;
}

.notification-item {
  transition: all 0.3s ease;
}

.notification-unread {
  background: rgba(255, 193, 7, 0.1);
  border-left: 4px solid #FFC107;
}

.notification-new {
  animation: notificationPulse 1s ease-in-out infinite;
  background: rgba(33, 150, 243, 0.2) !important;
}

@keyframes notificationPulse {
  0%, 100% {
    background: rgba(33, 150, 243, 0.1);
    transform: scale(1);
  }
  50% {
    background: rgba(33, 150, 243, 0.3);
    transform: scale(1.02);
  }
}

.notification-badge {
  animation: badgeBlink 1.5s ease-in-out infinite;
}

@keyframes badgeBlink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.stat-card {
  transition: transform 0.3s ease;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-4px);
}

.chart-container {
  min-height: 300px;
}

.activity-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 250px;
  gap: 8px;
}

.chart-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.bar-container {
  height: 200px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  cursor: pointer;
}

.bar {
  width: 80%;
  background: linear-gradient(180deg, #FFC107 0%, #FF9800 100%);
  border-radius: 8px 8px 0 0;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 20px;
}

.bar:hover {
  opacity: 0.8;
  transform: scaleY(1.05);
}

.bar-label {
  margin-top: 8px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

.donut-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}

.activity-item,
.user-item,
.notification-item {
  transition: background 0.2s ease;
  cursor: pointer;
}

.activity-item:hover,
.user-item:hover,
.notification-item:hover {
  background: rgba(255, 193, 7, 0.05);
}

.notification-unread {
  background: rgba(255, 193, 7, 0.02);
  border-left: 4px solid #FFC107;
}

.status-item {
  padding: 16px;
  border-radius: 8px;
  background: rgba(255, 193, 7, 0.05);
}

/* Gradient animation for pending users card */
.pending-users-card {
  position: relative;
}

.gradient-animation {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 193, 7, 0.1) 30%,
    rgba(255, 193, 7, 0.2) 50%,
    rgba(255, 193, 7, 0.1) 70%,
    transparent 100%
  );
  animation: slideGradient 2s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}

@keyframes slideGradient {
  0% {
    left: -100%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    left: 100%;
    opacity: 0;
  }
}

.pending-users-card .v-card-title,
.pending-users-card .v-card-text {
  position: relative;
  z-index: 2;
}
</style>
